'use client';

import Link from 'next/link';

import { useEffect, useRef, useState } from 'react';

import { useSiteSettingsContext } from '@/core/providers/site-settings-provider';
import { cn } from '@/lib/utils';
import { extractPhoneNumber } from '@/shared/actions/format.builder';
import type { ILinkData, ILinkSectionData } from '@/shared/actions/navigation.service';
import { getSearchPagePath } from '@/shared/components/header/shared/get-search-page-path';
import { useHeaderStore } from '@/shared/components/header/stores/header-store-provider';
import { Icon } from '@/shared/ui/icon/icon';
import { InputSearch } from '@/shared/ui/input/input-search';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
} from '@/shared/ui/navigation-menu';

import { MenuOverlay } from './menu-overlay';

export interface IServiceMenuProps {
    serviceMenu: (ILinkData | ILinkSectionData)[];
    searchButtonLabel: string;
    resetButtonLabel: string;
    searchPlaceholder?: string;
    searchPage?: string;
    phone?: string;
}
export function ServiceMenu({
    serviceMenu,
    searchButtonLabel,
    resetButtonLabel,
    searchPlaceholder,
    phone,
}: IServiceMenuProps) {
    const [searchBarOpen, setSearchBarOpen] = useState(false);
    const searchBtnRef = useRef<HTMLDivElement>(null);
    const { overlayNotice, toggleOverlayNotice } = useHeaderStore(state => state);

    const { locale } = useSiteSettingsContext();
    const searchPagePath = getSearchPagePath(locale?.toString());

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (searchBtnRef.current && !searchBtnRef.current.contains(event.target as Node) && searchBarOpen) {
                setSearchBarOpen(false);
            }
        }
        if (searchBarOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [searchBarOpen]);

    return (
        <div className='relative flex h-10 items-center gap-6'>
            <div
                className={cn('flex items-center gap-6', {
                    invisible: searchBarOpen,
                })}>
                {overlayNotice.btnLabel && (
                    <button className='flex h-full items-center gap-1' onClick={toggleOverlayNotice}>
                        <div
                            className='bold-link-on-hover text-menu-secondary-selected before:text-menu-secondary before:text-subtle after:text-subtle after:text-menu-secondary-selected w-max cursor-pointer leading-3'
                            data-text={overlayNotice.btnLabel}>
                            {overlayNotice.btnLabel}
                        </div>

                        <Icon name='IcChevronForward' className='relative top-0.5 size-3' />
                    </button>
                )}

                <NavigationMenu className='h-10'>
                    <NavigationMenuList className='flex h-full items-center gap-6'>
                        {serviceMenu.map(item => (
                            <NavigationMenuItem key={`NavigationMenuItem-${item.id}`} className='h-full'>
                                <NavigationMenuTrigger
                                    value={item.title}
                                    className='data-[state=open]:bold-link-on-active bold-link-on-hover text-menu-secondary-selected before:text-menu-secondary before:text-subtle after:text-subtle after:text-menu-secondary-selected h-full w-max shrink-0 cursor-pointer'
                                    data-text={item?.title}
                                    asChild={!('isLinkSection' in item)}>
                                    {'isLinkSection' in item ? (
                                        item?.title
                                    ) : (
                                        <Link href={item.internalLink || item.url || '#'} className='h-full'>
                                            {item.title}
                                        </Link>
                                    )}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent id='NavigationMenuContent' data-fullwith='false'>
                                    {'isLinkSection' in item && <MenuOverlay item={item} />}
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                        ))}
                    </NavigationMenuList>
                </NavigationMenu>
            </div>

            <div className='relative'>
                <button
                    type='button'
                    aria-label={searchButtonLabel}
                    className={cn(
                        'bg-surface-gray-lightest flex size-8 cursor-pointer items-center justify-center rounded-full',
                        {
                            'opacity-0 transition-opacity duration-300': searchBarOpen,
                        },
                    )}
                    onClick={() => setSearchBarOpen(true)}>
                    <Icon name='IcSearch' />
                </button>

                {searchBarOpen && (
                    <div ref={searchBtnRef}>
                        <InputSearch
                            placeholder={searchPlaceholder}
                            className='animate-in slide-in-from-right-5 fade-in absolute top-[-4px] right-0 h-10 w-88 duration-600'
                            classesNames={{ inputClass: 'h-10', iconClass: 'size-5' }}
                            searchPage={searchPagePath}
                            autoFocus={true}
                            searchButtonLabel={searchButtonLabel}
                            resetButtonLabel={resetButtonLabel}
                        />
                    </div>
                )}
            </div>
            {phone && (
                <div className='absolute top-13 right-14'>
                    {(() => {
                        const phoneData = extractPhoneNumber(phone);
                        return (
                            <a className='flex items-center gap-2' href={phoneData.href}>
                                <Icon name='IcCall' className='size-4' />
                                <span className='text-button-sm-500'>{phoneData.formatted}</span>
                            </a>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}
